// src/data/offProperty.js
//
// THE OFF-PROPERTY VIEW - master list 3.1, the SECOND HALF of ISS-39. Ruling 177 built the pure
// model (src\data\appealLadder.js); this is the read/write layer that ISS-39 said the platform has
// never had a screen for, and the panel (src\components\OffPropertyPanel.js) draws what this returns.
//
// Taken under PD-19 and withdrawable by number (see the day's rulings file).
//
// ---------------------------------------------------------------------------------------------
// WHAT THIS FILE DOES, AND THE THREE THINGS IT DELIBERATELY REFUSES TO DO.
//
// appealLadder.js is the model: recordAct(), standing(), answerDue(), appealTimeline(). It reads no
// clock and no agreement of its own - "the page draws and writes; this decides." This file is the
// page's half: it assembles the acts for ONE claim, hands them to appealLadder, and hands the result
// to the panel. It writes nothing itself - the panel writes, through claimPatch()/arrayUnion, so the
// acts inherit the claim's own read/write scoping (firestore.rules claims/{id}) with no new rule.
//
//   1. IT DOES NOT DUPLICATE PRESENTMENT (SS XI). Serving the claim on the carrier is already
//      recorded by ruling 70 (src\data\claimPresentment.js) in the claim's own fields
//      (presentedOn / presentedBy / presentmentMethod). That is the presentment act. This file
//      PROJECTS a ladder `presented` act from those fields for the timeline to run on - it never
//      asks an officer to record presentment a second time, and appealActs never holds a `presented`.
//      A second writer of "was this served" would be the duplicated-authority defect §XI names.
//
//   2. IT DOES NOT COMPUTE THE CARRIER ANSWER DUE DATE. appealLadder.answerDue refuses a date with no
//      recorded day-count, and this file passes it none (carrierAnswerDays: null) ON PURPOSE. The
//      per-agreement carrier clocks DO exist now in claimRequirements.js (A-8, 2026-09-21, read
//      verbatim at source) - but they do not map cleanly onto the ladder's two-act clock (present /
//      appeal): CSX answers in 60 days after filing and 75 after appeal; Conrail's later carrier
//      clock runs from the date discussed at conference, which is not a ladder-restart act at all.
//      A single day-count fed to answerDue would therefore compute a WRONG date after an appeal, and
//      a deadline from the wrong clock is worse than none (appealLadder's own rule, PD-0). So the
//      due date stays UNKNOWN, and instead the agreement's carrier provision is REPRODUCED VERBATIM
//      beside the ladder with its cite and its on-silence consequence (PD-16) - which is the highest-
//      value protection stated honestly, and lets the officer read the clock off the agreement rather
//      than off a number the platform guessed. Turning on a computed date is SME/agreement work and
//      Bob's call (see the task list) - it is flagged, not invented.
//
//   3. IT DOES NOT ASSESS THE CARRIER'S DECLINATION. The carrier's stated grounds are captured into
//      the carrier_answered act's reasonGiven as text a PERSON entered or confirmed - never a machine
//      verdict, never sent to any third-party AI (the declination names the member and the discipline;
//      LOCAL ONLY, ruling of record). Its integrity check against the stored bytes (EXH-1) belongs to
//      the exhibit-ingestion task, not here.
//
// Pure module: no React, no Firestore.

import {
  recordAct, sortActs, standing, appealTimeline, ACTS, ACT_KINDS,
  ANSWER_OUTCOMES, SUBMISSION_KINDS, AWARD_OUTCOMES,
} from './appealLadder';
import { isPresented, presentmentSummary, methodLabel } from './claimPresentment';
import { getClaimRequirements } from './claimRequirements';
import { carrierLimits } from './carrierAnswerLimits';

const F = Object.freeze;

// The acts the OFF-PROPERTY PANEL records itself. `presented` is NOT here: it is owned by ruling 70
// and projected in, so nobody records presentment twice. `award_complied` / `award_not_complied` and
// `carrier_answered` are carrier-side facts recorded by an officer of record from what actually
// happened - the platform never asserts them on its own (ISS-39: silence is a flag, not an answer).
export const PANEL_ACT_KINDS = F(ACT_KINDS.filter((k) => k !== 'presented'));

function nonEmpty(v) { return v !== null && v !== undefined && String(v).trim() !== ''; }

// ---------------------------------------------------------------------------------------------
// THE PROJECTED PRESENTMENT ACT
//
// Built directly, NOT through recordAct(): recordAct requires servedOn (the carrier officer the
// claim was served ON), and ruling 70's presentment record does not capture it - it records who
// SERVED it and how, not who received it. So a projection through recordAct would be refused for a
// presentment that really happened. The timeline only reads `kind` and `on`, so a minimal projected
// act is enough for standing and the clock to run; the missing recipient is surfaced, never guessed.
export function projectedPresentedAct(claim) {
  if (!isPresented(claim)) return null;
  const on = String(claim.presentedOn || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(on)) return null;   // an unreadable date does not start a clock
  return {
    kind: 'presented',
    side: 'organization',
    on,
    projected: true,                       // this act is derived from ruling 70, not stored in appealActs
    servedOn: '',                          // NOT ON RECORD - the presentment fields never captured it
    means: String(claim.presentmentMethod || '').trim() || '',
    note: null,
  };
}

// Was who-at-the-carrier-received-it captured? appealLadder holds that this is the fact that proves
// the clock started. It is never on the ruling-70 record, so this is always a gap to state, not fill.
export function presentmentRecipientGap(claim) {
  if (!isPresented(claim)) return null;
  return 'The record names who served the claim and how, but not who at the carrier received it. '
       + 'The appeal ladder treats the recipient as the fact that proves the carrier’s answer clock '
       + 'started; it is not on this platform’s presentment record and is not guessed here.';
}

// ---------------------------------------------------------------------------------------------
// THE FULL SET OF ACTS FOR ONE CLAIM
//
// The projected presentment act (if any) plus the acts stored on the claim. Stored acts are filtered
// to the ones the ladder knows and, defensively, any stored `presented` is dropped so a bad write can
// never produce two presentments - the projection is the single source (§XI).
export function claimActs(claim) {
  const stored = Array.isArray(claim && claim.appealActs) ? claim.appealActs : [];
  const kept = stored.filter((a) => a && ACTS[a.kind] && a.kind !== 'presented');
  const presented = projectedPresentedAct(claim);
  return sortActs(presented ? [presented].concat(kept) : kept);
}

// ---------------------------------------------------------------------------------------------
// THE CARRIER ANSWER CLOCK - as the AGREEMENT states it, verbatim, never as a computed date.
//
// Read from claimRequirements.clocks (party: 'carrier'), which A-8 extracted verbatim at cited source
// lines on 2026-09-21. Each is returned with the exact on-silence consequence the agreement attaches,
// because that consequence - "the claim shall be allowed as presented" and its kin - is the whole
// value of the deadline (PD-16: reproduced exactly, its cite carried).
export function carrierClockReference(claim) {
  const req = getClaimRequirements(claim && claim.applicableAgreement);
  const clocks = (req && Array.isArray(req.clocks)) ? req.clocks : [];
  const carrier = clocks.filter((c) => c && c.party === 'carrier');
  return {
    agreement: (claim && claim.applicableAgreement) || null,
    claimsRule: (req && req.claimsRule) || null,
    // 'recorded' | 'not_recorded' - the agreement is catalogued but carries no carrier clock, vs the
    // platform holds nothing for this agreement at all. A screen must tell those two apart.
    state: req ? (carrier.length ? 'recorded' : 'catalogued_no_carrier_clock') : 'not_recorded',
    clocks: carrier.map((c) => F({
      label: c.label,
      days: Number.isFinite(c.days) ? c.days : null,
      months: Number.isFinite(c.months) ? c.months : null,
      from: c.from || null,
      onSilence: nonEmpty(c.onSilence) ? String(c.onSilence).trim() : null,   // verbatim, or null - never paraphrased
      cite: c.cite || null,
    })),
  };
}

// The `limits` object handed to appealLadder.answerDue. carrierAnswerDays is null BY DESIGN (reason 2
// in the header), so answerDue returns UNKNOWN - and `provision` names the agreement's own carrier
// clock so the UNKNOWN message points at the right provision rather than the generic catalogue id.
export function answerLimits(claim) {
  const ref = carrierClockReference(claim);
  const first = ref.clocks.length ? ref.clocks[0] : null;
  // Ruling 266 (2026-10-02): the day-counts Bob CONFIRMED on 2026-09-25 (carrierAnswerLimits.js, §414/§415)
  // now reach the claim page too - until today only the War Room and the union calendar read them, so the
  // claim itself said "no due date" while the War Room showed one. An agreement not in that table still
  // yields null, and appealLadder.js still refuses to guess.
  const lim = carrierLimits(claim && claim.applicableAgreement);
  // Record 612 (Bob 10/8, decision 28): the agreement's own default-allowance words, for appealLadder's one wording.
  // The carrierDefault profile (claimRequirements.js) is the one source. It is admitted only when whole - the test
  // carrierDefault.defaultProfileOf() applies, on the same fields - and its quote travels verbatim. No profile: null,
  // and the wording names the provision instead of quoting.
  const cdReq = getClaimRequirements(claim && claim.applicableAgreement);
  const cd = cdReq && cdReq.carrierDefault;
  const cdOk = !!cd && Number.isInteger(cd.days) && cd.days > 0 && nonEmpty(cd.rule) && nonEmpty(cd.quote) && nonEmpty(cd.source);
  return {
    defaultAllowanceRule: cdOk ? cd.rule : null,
    defaultAllowanceQuote: cdOk ? cd.quote : null,
    carrierAnswerDays: lim.carrierAnswerDays,
    appealAnswerDays: lim.appealAnswerDays,
    appealAnswerStated: lim.appealAnswerStated,   // forensic audit 2026-10-16
    appealAnswerFrom: lim.appealAnswerFrom,       // forensic audit 2026-10-16: CSX counts the appeal answer from receipt
    conferenceDecisionDays: lim.conferenceDecisionDays,
    provision: first && first.cite ? first.cite : (ref.claimsRule || 'CLAIM-carrier-response-limits'),
    defaultAllowanceProvision: first && first.onSilence
      ? (first.cite || 'CLAIM-carrier-response-limits-default-allowance')
      : 'CLAIM-carrier-response-limits-default-allowance',
  };
}

// ---------------------------------------------------------------------------------------------
// THE WHOLE OFF-PROPERTY PICTURE FOR ONE CLAIM - what the panel draws, computed once, here.
export function offPropertyView(claim, today) {
  const acts = claimActs(claim);
  const timeline = appealTimeline(acts, answerLimits(claim), today);
  return {
    presented: isPresented(claim) ? presentmentSummary(claim) : { recorded: false },
    recipientGap: presentmentRecipientGap(claim),
    timeline,                              // { acts, standing, presented, answer, silence, gaps }
    carrierClock: carrierClockReference(claim),
    hasActs: acts.length > 0,
  };
}

// ---------------------------------------------------------------------------------------------
// RECORDING AN ACT - validate through appealLadder.recordAct(), then attach the two off-property
// concerns appealLadder does not model: the link to a stored declination document, and the
// provenance of reasonGiven. recordAct() is left UNMODIFIED (it is the proven ruling-177 module);
// the extra fields are attached to the validated record, and appealLadder ignores fields it does
// not read. The panel writes the returned record with arrayUnion(claimPatch(...)).
//
// `declination` (carrier_answered only), all optional:
//   docPath, docName  - a document ALREADY on the claim (claim.documents) that states the grounds.
//                       Resolved against the live document list by the caller; a dangling link is
//                       never stored. When the exhibit receptor/ingestion lands this is where a
//                       stored, integrity-checked declination attaches (EXH-1 is that task's).
//   reasonSource      - 'officer_entered' | 'confirmed_from_document'. Either way a PERSON owns the
//                       grounds; nothing is machine-adopted and nothing left the platform to produce it.
export function buildAct(input, declination) {
  const kind = input && input.kind;
  if (kind === 'presented') {
    // Presentment is ruling 70's, projected in - it is never recorded through this panel (§XI).
    return { ok: false, errors: ['Presentment is recorded on the “Served on the carrier” control, not here. '
      + 'The appeal ladder reads it from there so it is not recorded twice.'], record: null };
  }
  const r = recordAct(input);
  if (!r.ok) return r;
  const rec = { ...r.record };
  if (rec.kind === 'carrier_answered') {
    const d = declination || {};
    if (nonEmpty(d.docPath)) {
      rec.declinationDocPath = String(d.docPath).trim();
      rec.declinationDocName = nonEmpty(d.docName) ? String(d.docName).trim() : null;
    }
    // reasonGiven is a PROPOSAL a person owns; the provenance is ALWAYS recorded - never left blank,
    // never a machine verdict. Absent any signal it is the officer's own entry; only an explicit
    // confirm-from-document says otherwise. (When the ingestion pipeline lands it may PROPOSE grounds
    // for a person to confirm; that still resolves to one of these two, never to an unowned value.)
    const src = String(d.reasonSource || '').trim();
    rec.reasonSource = (src === 'confirmed_from_document') ? 'confirmed_from_document' : 'officer_entered';
  }
  return { ok: true, errors: [], record: rec };
}

// Re-exported so the panel imports one module. The closed sets and labels stay in appealLadder -
// this does not restate them (§XI).
export { ANSWER_OUTCOMES, SUBMISSION_KINDS, AWARD_OUTCOMES, ACTS, standing, methodLabel };
// Ruling 268 (2026-10-02): the one outcome box, re-exported for the panel - defined once, in appealLadder.js.
export { ANSWER_BOX, answerBoxLabel, answerBox, RULING_268_OUTCOME_BOX } from './appealLadder';
